<template>
  <!-- PR-COACH-ROUTE-FIX: SageAvatar — the small "S" badge shown next to
       Sage's chat bubbles in the onboarding Coach. The Coach component
       (AIWorkAchievementsCoach.vue) was authored to import this file from
       day one but the file itself never made it into the repo — so when
       the missing route registration was fixed and Vue actually started
       compiling the Coach chunk, the build broke on the dangling import.
       This component fills that gap with the minimal API the Coach
       expects: a `state` prop that drives a subtle animation, and a
       `size` prop for the diameter. -->
  <div
    class="sage-avatar"
    :class="stateClass"
    :style="sizeStyle"
    :aria-label="ariaLabel"
    role="img"
  >
    <span class="sage-avatar-letter">S</span>
    <!-- Thinking state — a soft pulsing ring around the badge so the
         user has a visual signal that Sage is composing a reply. The
         ring is positioned absolutely so it doesn't change the layout
         box and bump surrounding chat content. -->
    <span v-if="state === 'thinking'" class="sage-avatar-ring"></span>
  </div>
</template>

<script>
export default {
  name: "SageAvatar",
  props: {
    // One of: "default" | "thinking" | "presenting". Any unknown value
    // falls through to default. Keeps the contract loose so future Coach
    // states can be added by the orchestrator without breaking here.
    state: {
      type: String,
      default: "default"
    },
    // Diameter in pixels. The Coach passes 28 or 32 depending on context
    // (presenting cards are slightly smaller). Default 32 mirrors the
    // most common usage.
    size: {
      type: Number,
      default: 32
    }
  },
  computed: {
    stateClass() {
      switch (this.state) {
        case "thinking":
          return "sage-avatar--thinking";
        case "presenting":
          return "sage-avatar--presenting";
        default:
          return "sage-avatar--default";
      }
    },
    sizeStyle() {
      // Font size scales with diameter so the "S" stays well-proportioned
      // across the 24–48px range we'd reasonably use this at.
      const fontPx = Math.max(11, Math.round(this.size * 0.45));
      return {
        width: this.size + "px",
        height: this.size + "px",
        fontSize: fontPx + "px"
      };
    },
    ariaLabel() {
      switch (this.state) {
        case "thinking":
          return "Sage is thinking";
        case "presenting":
          return "Sage";
        default:
          return "Sage";
      }
    }
  }
};
</script>

<style scoped>
/* Solid circular badge in Resumation's primary blue, white "S" centered.
   Default state is static. Thinking adds a pulsing ring sibling.
   Presenting gets a subtle elevated glow to signal a "here's what I
   captured" moment. */
.sage-avatar {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%);
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  /* Light shadow gives the badge dimension against the chat-bubble
     background — same depth treatment used on other primary chips. */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: box-shadow 200ms ease, transform 200ms ease;
}

/* The letter sits in its own span so we can animate the badge without
   wiggling the glyph. */
.sage-avatar-letter {
  line-height: 1;
  /* Slight vertical optical correction — purely visual; "S" reads as
     slightly low-baseline at small sizes without a nudge. */
  transform: translateY(-1px);
}

/* Thinking state: an outer ring that pulses. We render it as an absolute
   pseudo-sibling so the layout box stays the same as the default state. */
.sage-avatar-ring {
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border-radius: 50%;
  border: 2px solid rgba(124, 58, 237, 0.55);
  animation: sage-avatar-pulse 1.4s ease-in-out infinite;
  pointer-events: none;
}

.sage-avatar--presenting {
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.18),
    0 2px 6px rgba(0, 0, 0, 0.18);
}

@keyframes sage-avatar-pulse {
  0% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(1.12);
  }
  100% {
    opacity: 0.85;
    transform: scale(1);
  }
}

/* Dark mode — slightly brighter purple so the badge holds presence
   against the darker chat surface. */
.theme--dark .sage-avatar {
  background: linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%);
}
.theme--dark .sage-avatar-ring {
  border-color: rgba(167, 139, 250, 0.65);
}

/* Respect users who've asked the OS to dial down motion — drop the
   pulse animation entirely. The ring stays visible (so the thinking
   signal is still there), just static. */
@media (prefers-reduced-motion: reduce) {
  .sage-avatar-ring {
    animation: none;
  }
}
</style>
